import api from './api';

export interface Company {
  id: string;
  social_name: string;
  trade_name: string;
  cnpj: string;
  logo?: string | null;
  menu_position?: string | null;
  has_sped: boolean;
  has_dfe: boolean;
}

export interface User {
  id: string;
  username: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: Company | null;
  company_name?: string | null;
  phone?: string | null;
  cpf?: string | null;
  avatar?: string | null;
  cargo?: string | null;
  departamento?: string | null;
  is_admin: boolean;
  is_company_admin: boolean;
  is_manager: boolean;
  is_supervisor: boolean;
  is_operator: boolean;
  is_contador: boolean;
  perfil_industrializacao?: string | null;
  sped_perfil?: string | null;
  dfe_perfil?: string | null;
  is_active: boolean;
  is_staff: boolean;
  date_joined?: string | null;
  ultimo_acesso?: string | null;
}

export interface LoginResponse {
  token: string;
  access: string;
  user: User;
  perfil_contador?: unknown;
}

export interface MeResponse {
  user: User;
}

export interface RegisterInput {
  username: string;
  email: string;
  password: string;
  password_confirm: string;
  first_name?: string;
  last_name?: string;
  company_id?: string | null;
  phone?: string;
  cpf?: string;
  cargo?: string;
  departamento?: string;
}

export interface ProfileInput {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string | null;
  cpf?: string | null;
  avatar?: string | null;
  cargo?: string | null;
  departamento?: string | null;
}

export interface ChangePasswordInput {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}

/**
 * Rota pos-login, baseada no perfil do usuario.
 * Equivalente a `postLoginRoute` do stores/auth.js (Vue).
 */
export function postLoginRoute(user: User | null): string {
  if (!user) return '/login';
  if (user.is_admin) return '/dashboard';
  const perfilInd = user.perfil_industrializacao || 'COMPLETO';
  if (perfilInd !== 'BLOQUEADO') return '/producao/dashboard';
  return '/dashboard';
}

const authService = {
  async login(username: string, password: string): Promise<LoginResponse> {
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    const response = await api.post<LoginResponse>('/auth/login', {
      username,
      password,
    });

    const token = response.data.token || response.data.access;
    if (token) {
      localStorage.setItem('token', token);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  },

  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  async me(): Promise<MeResponse> {
    const response = await api.get<User>('/auth/me');
    return { user: response.data };
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem('token');
  },

  getToken(): string | null {
    return localStorage.getItem('token');
  },

  getStoredUser(): User | null {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  },

  async register(data: RegisterInput): Promise<LoginResponse> {
    const response = await api.post<LoginResponse>('/auth/register', data);
    return response.data;
  },

  async getProfile(): Promise<User> {
    const response = await api.get<User>('/auth/profile');
    return response.data;
  },

  async updateProfile(data: ProfileInput): Promise<User> {
    const response = await api.put<User>('/auth/profile', data);
    return response.data;
  },

  async changePassword(data: ChangePasswordInput): Promise<{ message: string }> {
    const response = await api.post('/auth/change-password', data);
    return response.data;
  },
};

export default authService;
